import type { CSSProperties, FormEvent, ReactNode } from "react";

import {
  BODY_MAX,
  BUSINESS_HOURS_NOTE,
  FALLBACK_NOTE,
  formatCentral,
  replyState,
  RESPONSE_TARGETS,
  SCREENSHOT_TYPES,
  statusLabel,
  targetLine,
} from "../../lib/helpDesk/helpDesk.mjs";
import type {
  HelpMessage,
  HelpStatus,
  HelpTicket,
  HelpUrgency,
} from "../../lib/helpDesk/helpDesk.mjs";

/**
 * The help desk's presentational pieces (issue #1096). No data access here:
 * HelpButton, the My requests page and the admin panel own the state and the
 * Supabase calls, and scripts/help-desk-components.test.mjs renders these
 * with plain props.
 */

const LABEL: CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--text)" };
const HINT: CSSProperties = { fontSize: 12, color: "var(--text-2)" };
const ERROR: CSSProperties = { fontSize: 12, color: "var(--flag-red)" };

export const BUTTON: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  fontWeight: 600,
  padding: "7px 14px",
  borderRadius: 8,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#fff",
  cursor: "pointer",
};

export const BUTTON_QUIET: CSSProperties = {
  ...BUTTON,
  background: "transparent",
  color: "var(--brand-deep)",
};

const STATUS_COLORS: Record<HelpStatus, { bg: string; fg: string }> = {
  open: { bg: "var(--domain-operations-wash)", fg: "var(--domain-operations)" },
  in_progress: { bg: "var(--domain-disposal-wash)", fg: "var(--brand-deep)" },
  waiting_on_you: { bg: "var(--flag-yellow-wash)", fg: "var(--flag-yellow)" },
  resolved: { bg: "var(--bg)", fg: "var(--text-2)" },
};

export function StatusPill({ status }: { status: HelpStatus }) {
  const c = STATUS_COLORS[status] ?? STATUS_COLORS.open;
  return (
    <span
      data-status={status}
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 99,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      {statusLabel(status)}
    </span>
  );
}

export function UrgentPill() {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 99,
        background: "var(--flag-red-wash)",
        color: "var(--flag-red)",
      }}
    >
      Urgent
    </span>
  );
}

export interface HelpRequestFormProps {
  pageLabel: string;
  browserLabel: string;
  body: string;
  urgency: HelpUrgency;
  screenshotName: string | null;
  errors: { body?: string; screenshot?: string };
  submitting: boolean;
  error: string | null;
  staffPreview: boolean;
  onBodyChange: (value: string) => void;
  onUrgencyChange: (value: HelpUrgency) => void;
  onScreenshotChange: (file: File | null) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function HelpRequestForm(props: HelpRequestFormProps) {
  const {
    pageLabel,
    browserLabel,
    body,
    urgency,
    screenshotName,
    errors,
    submitting,
    error,
    staffPreview,
  } = props;
  function submit(event: FormEvent) {
    event.preventDefault();
    props.onSubmit();
  }
  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 14 }} aria-label="Help request">
      {staffPreview ? (
        <div style={{ ...HINT, color: "var(--flag-yellow)" }}>
          Staff preview: this customer cannot see the Help button yet.
        </div>
      ) : null}
      <label style={{ display: "grid", gap: 6 }}>
        <span style={LABEL}>What is wrong, or what do you need?</span>
        <textarea
          name="body"
          value={body}
          maxLength={BODY_MAX}
          rows={6}
          required
          aria-invalid={errors.body ? true : undefined}
          onChange={(e) => props.onBodyChange(e.target.value)}
          style={{
            font: "inherit",
            fontSize: 14,
            padding: 10,
            borderRadius: 8,
            border: "1px solid var(--border)",
            resize: "vertical",
          }}
        />
        {errors.body ? <span style={ERROR}>{errors.body}</span> : null}
      </label>

      <fieldset style={{ border: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
        <legend style={{ ...LABEL, padding: 0, marginBottom: 6 }}>How urgent is it?</legend>
        <label style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <input
            type="radio"
            name="urgency"
            value="normal"
            checked={urgency === "normal"}
            onChange={() => props.onUrgencyChange("normal")}
          />
          <strong style={{ fontSize: 13 }}>Normal</strong>
          <span style={HINT}>{RESPONSE_TARGETS.normal}</span>
        </label>
        <label style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <input
            type="radio"
            name="urgency"
            value="urgent"
            checked={urgency === "urgent"}
            onChange={() => props.onUrgencyChange("urgent")}
          />
          <strong style={{ fontSize: 13 }}>Urgent</strong>
          <span style={HINT}>{RESPONSE_TARGETS.urgent}</span>
        </label>
        <span style={HINT}>{BUSINESS_HOURS_NOTE}</span>
      </fieldset>

      <label style={{ display: "grid", gap: 6 }}>
        <span style={LABEL}>Screenshot (optional)</span>
        <input
          type="file"
          name="screenshot"
          accept={SCREENSHOT_TYPES.join(",")}
          onChange={(e) => props.onScreenshotChange(e.target.files?.[0] ?? null)}
        />
        {screenshotName ? <span style={HINT}>Attached: {screenshotName}</span> : null}
        {errors.screenshot ? <span style={ERROR}>{errors.screenshot}</span> : null}
      </label>

      <div style={{ ...HINT, display: "grid", gap: 2 }}>
        <span>Sent with your request so we can see what you saw:</span>
        <span>Page: {pageLabel}</span>
        <span>Browser: {browserLabel}</span>
        <span>Your name, email and company.</span>
      </div>

      {error ? (
        <div role="alert" style={ERROR}>
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" onClick={props.onCancel} style={BUTTON_QUIET} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" style={BUTTON} disabled={submitting}>
          {submitting ? "Sending..." : "Send request"}
        </button>
      </div>
      <div style={{ ...HINT, fontSize: 11 }}>{FALLBACK_NOTE}</div>
    </form>
  );
}

export interface HelpConfirmationProps {
  ticket: Pick<
    HelpTicket,
    | "number"
    | "status"
    | "resolved_at"
    | "first_response_at"
    | "created_at"
    | "urgency"
    | "urgent_at"
  >;
  now?: number;
  onViewRequests: () => void;
  onClose: () => void;
}

export function HelpConfirmation({ ticket, now, onViewRequests, onClose }: HelpConfirmationProps) {
  return (
    <div role="status" style={{ display: "grid", gap: 12 }}>
      <div style={{ fontSize: 16, fontWeight: 700 }}>Request #{ticket.number} sent</div>
      <div style={{ fontSize: 14 }}>
        Meridian has it. {targetLine(ticket, now)}.
        {ticket.urgency === "urgent" ? " Marked urgent." : ""}
      </div>
      <div style={HINT}>You can follow it and reply under My requests.</div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" onClick={onViewRequests} style={BUTTON_QUIET}>
          View my requests
        </button>
        <button type="button" onClick={onClose} style={BUTTON}>
          Done
        </button>
      </div>
    </div>
  );
}

export interface RequestListProps {
  tickets: HelpTicket[];
  selectedId: string | null;
  showRequester: boolean;
  showCustomer?: boolean;
  now?: number;
  onSelect: (id: string) => void;
}

export function RequestList(props: RequestListProps) {
  const { tickets, selectedId, showRequester, showCustomer = false, now } = props;
  if (tickets.length === 0) {
    return <div style={{ ...HINT, padding: 16 }}>No requests yet.</div>;
  }
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
      {tickets.map((t) => {
        const selected = t.id === selectedId;
        return (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => props.onSelect(t.id)}
              aria-pressed={selected}
              style={{
                font: "inherit",
                textAlign: "left",
                width: "100%",
                display: "grid",
                gap: 4,
                padding: "10px 12px",
                borderRadius: 10,
                cursor: "pointer",
                border: selected ? "1px solid var(--brand-teal)" : "1px solid var(--border)",
                background: selected ? "var(--domain-disposal-wash)" : "var(--panel)",
                color: "var(--text)",
              }}
            >
              <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <strong style={{ fontSize: 13 }}>#{t.number}</strong>
                <StatusPill status={t.status} />
                {t.urgency === "urgent" ? <UrgentPill /> : null}
                {showCustomer && t.tenant ? (
                  <span style={{ ...HINT, fontWeight: 600 }}>{t.tenant.name}</span>
                ) : null}
              </span>
              <span
                style={{
                  fontSize: 13,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t.body.split("\n")[0]}
              </span>
              <span style={HINT}>
                {showRequester ? `${t.requester_name || t.requester_email || "Unknown"} · ` : ""}
                {targetLine(t, now)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export interface RequestThreadProps {
  ticket: HelpTicket;
  messages: HelpMessage[];
  replyDraft: string;
  sending: boolean;
  error: string | null;
  canMarkUrgent: boolean;
  screenshotHref: string | null;
  staffControls?: ReactNode;
  /** Meridian staff: may always reply, and a reply does not reopen. */
  asMeridian?: boolean;
  now?: number;
  onReplyChange: (value: string) => void;
  onSendReply: () => void;
  onMarkUrgent: () => void;
}

export function RequestThread(props: RequestThreadProps) {
  const { ticket, messages, replyDraft, sending, error, canMarkUrgent, screenshotHref, now } =
    props;
  const reply = props.asMeridian
    ? { canReply: true, reopens: false, reason: null }
    : replyState(ticket, now);
  function submit(event: FormEvent) {
    event.preventDefault();
    props.onSendReply();
  }
  return (
    <article style={{ display: "grid", gap: 14 }} aria-label={`Request ${ticket.number}`}>
      <header style={{ display: "grid", gap: 6 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>Request #{ticket.number}</h2>
          <StatusPill status={ticket.status} />
          {ticket.urgency === "urgent" ? <UrgentPill /> : null}
        </div>
        <div style={HINT}>
          Sent {formatCentral(ticket.created_at)} by{" "}
          {ticket.requester_name || ticket.requester_email || "Unknown"}
          {ticket.page_title || ticket.page_path
            ? ` from ${ticket.page_title ?? ""}${ticket.page_title && ticket.page_path ? " " : ""}${
                ticket.page_path ? `(${ticket.page_path})` : ""
              }`
            : ""}
        </div>
        <div style={{ ...HINT, fontWeight: 600 }}>{targetLine(ticket, now)}</div>
        {canMarkUrgent && ticket.urgency !== "urgent" && ticket.status !== "resolved" ? (
          <div>
            <button type="button" onClick={props.onMarkUrgent} style={BUTTON_QUIET}>
              Mark urgent
            </button>
            <span style={{ ...HINT, marginLeft: 8 }}>{RESPONSE_TARGETS.urgent}, from now.</span>
          </div>
        ) : null}
        {props.staffControls}
      </header>

      <div
        style={{
          whiteSpace: "pre-wrap",
          fontSize: 14,
          padding: 12,
          borderRadius: 10,
          background: "var(--bg)",
        }}
      >
        {ticket.body}
      </div>
      {screenshotHref ? (
        <a href={screenshotHref} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
          View the screenshot
        </a>
      ) : null}

      <section style={{ display: "grid", gap: 10 }} aria-label="Replies">
        {messages.map((m) => (
          <div
            key={m.id}
            data-role={m.author_role}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              background:
                m.author_role === "meridian" ? "var(--domain-disposal-wash)" : "var(--panel)",
            }}
          >
            <div style={{ ...HINT, marginBottom: 4 }}>
              <strong style={{ color: "var(--text)" }}>
                {m.author_role === "meridian" ? "Meridian" : m.author_name || "You"}
              </strong>{" "}
              · {formatCentral(m.created_at)}
            </div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{m.body}</div>
          </div>
        ))}
      </section>

      {reply.canReply ? (
        <form onSubmit={submit} style={{ display: "grid", gap: 8 }} aria-label="Reply">
          <label style={{ display: "grid", gap: 6 }}>
            <span style={LABEL}>Reply</span>
            <textarea
              name="reply"
              value={replyDraft}
              rows={3}
              maxLength={BODY_MAX}
              onChange={(e) => props.onReplyChange(e.target.value)}
              style={{
                font: "inherit",
                fontSize: 14,
                padding: 10,
                borderRadius: 8,
                border: "1px solid var(--border)",
              }}
            />
          </label>
          {reply.reopens ? <span style={HINT}>Replying reopens this request.</span> : null}
          {error ? (
            <div role="alert" style={ERROR}>
              {error}
            </div>
          ) : null}
          <div>
            <button type="submit" style={BUTTON} disabled={sending || !replyDraft.trim()}>
              {sending ? "Sending..." : "Send reply"}
            </button>
          </div>
        </form>
      ) : (
        <div style={HINT}>{reply.reason}</div>
      )}
    </article>
  );
}
